import { ref, computed, onMounted, onUnmounted, type Ref } from "vue";
import { Facade } from "@/drawing";
import type { ObservationEntityRef } from "@/drawing/managers/ObservationsManager";
import { Logger } from "@/logger";

// Le contexte plan (condensé des entités envoyé à l'IA) vit désormais dans
// `utils/plan-analysis/plan-context.ts` — pur, partagé avec l'analyse du plan
// et couvert par les tests. Ré-exporté ici pour les consommateurs historiques.
export {
  buildPlanContext,
  type PlanEntityGroup,
  type ObservationPlanContext,
  type ObservationPlanContextMap,
} from "@/utils/plan-analysis/plan-context";

/**
 * Liaison plan↔observations côté canvas (lecture du DWG en mémoire).
 *
 * - `entitiesByObservation` : entités rattachées, groupées par `localId`
 *   d'observation (rescanné sur chaque changement d'historique, débouncé).
 * - `selectionHandles` : sélection courante (réactive).
 * - `selectedObservationIds` : observations dont au moins une entité est
 *   sélectionnée — sert au highlight inverse (plan → liste).
 * - `stampSelection` / `clearObservation` : écriture du rattachement.
 * - `locate` : sélectionne + zoome sur les entités d'une observation.
 */
export function useObservationPlanLinks(): {
  entitiesByObservation: Ref<Map<string, ObservationEntityRef[]>>;
  selectionHandles: Ref<string[]>;
  selectedObservationIds: Ref<string[]>;
  refresh: () => void;
  stampSelection: (observationLocalId: string) => number;
  stampHandles: (handles: string[], observationLocalId: string) => number;
  setMarkerPhotos: (handle: string, photoIds: readonly number[]) => void;
  detachHandles: (handles: string[]) => number;
  clearObservation: (observationLocalId: string) => number;
  locate: (observationLocalId: string) => void;
} {
  const entitiesByObservation = ref<Map<string, ObservationEntityRef[]>>(
    new Map()
  );
  const selectionHandles = ref<string[]>([]);

  let unsubscribeSelection: (() => void) | null = null;
  let historyHandler: (() => void) | null = null;
  let reloadTimer: ReturnType<typeof setTimeout> | null = null;

  /** Index inverse handle → localId d'observation (dérivé du scan). */
  const observationIdByHandle = computed(() => {
    const map = new Map<string, string>();
    for (const [obsId, refs] of entitiesByObservation.value) {
      for (const entityRef of refs) map.set(entityRef.handle, obsId);
    }
    return map;
  });

  const selectedObservationIds = computed(() => {
    const ids = new Set<string>();
    for (const h of selectionHandles.value) {
      const obsId = observationIdByHandle.value.get(h);
      if (obsId) ids.add(obsId);
    }
    return [...ids];
  });

  function refresh(): void {
    if (!Facade.isInitialized) return;
    entitiesByObservation.value = Facade.observations.listEntities();
  }

  /** Rescan débouncé — un drag génère une rafale de commandes. */
  function scheduleRefresh(): void {
    if (reloadTimer) clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => {
      reloadTimer = null;
      refresh();
    }, 250);
  }

  function stampHandles(handles: string[], observationLocalId: string): number {
    if (!Facade.isInitialized || handles.length === 0) return 0;
    const count = Facade.observations.setEntities(handles, observationLocalId);
    Logger.info(
      `Observations : stamped ${count} entities on observation ${observationLocalId}`
    );
    refresh();
    return count;
  }

  function setMarkerPhotos(handle: string, photoIds: readonly number[]): void {
    if (!Facade.isInitialized) return;
    Facade.observations.setMarkerPhotos(handle, photoIds);
    refresh();
  }

  function stampSelection(observationLocalId: string): number {
    return stampHandles(selectionHandles.value, observationLocalId);
  }

  function detachHandles(handles: string[]): number {
    if (!Facade.isInitialized || handles.length === 0) return 0;
    const count = Facade.observations.setEntities(handles, null);
    refresh();
    return count;
  }

  function clearObservation(observationLocalId: string): number {
    if (!Facade.isInitialized) return 0;
    const count = Facade.observations.clear(observationLocalId);
    Logger.info(
      `Observations : cleared ${count} entities from observation ${observationLocalId}`
    );
    refresh();
    return count;
  }

  /** Sélectionne + zoome sur les entités de l'observation. */
  function locate(observationLocalId: string): void {
    const refs = entitiesByObservation.value.get(observationLocalId);
    if (!refs?.length) return;
    Facade.selection.highlightByHandles(refs.map((r) => r.handle));
  }

  onMounted(() => {
    // Facade pas prête (ex. instance périmée par le HMR) : panneau vide plutôt
    // que planter — même garde que le fil Activité.
    if (!Facade.isInitialized) return;

    unsubscribeSelection = Facade.selection.onChange((handles) => {
      selectionHandles.value = handles;
    });
    selectionHandles.value = Facade.selection.handles;

    historyHandler = (): void => scheduleRefresh();
    Facade.history.onChange(historyHandler);
    refresh();
  });

  onUnmounted(() => {
    unsubscribeSelection?.();
    if (historyHandler && Facade.isInitialized) {
      Facade.history.offChange(historyHandler);
    }
    if (reloadTimer) clearTimeout(reloadTimer);
  });

  return {
    entitiesByObservation,
    selectionHandles,
    selectedObservationIds,
    refresh,
    stampSelection,
    stampHandles,
    setMarkerPhotos,
    detachHandles,
    clearObservation,
    locate,
  };
}
